import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui";
import type { PreflightMarketKind } from "@nexus/contracts";
import { groupMarketsByKind } from "../../lib/market-order";
import { ManageMarketsDialog } from "../ManageMarketsDialog/ManageMarketsDialog";
import { MarketIcon } from "../MarketIcon/MarketIcon";
import type { PreflightMarketWithCount } from "../../types/preflight.types";

/** `null` is the exclusive all-markets scope, which is the hub default (R7). */
export type MarketScope = string[] | null;

type SharedMarketSelectProps = {
  markets: PreflightMarketWithCount[];
  disabled?: boolean;
  /** Trigger height. The hub header pairs it with a primary CTA, so it uses `default`. */
  size?: "sm" | "default";
  /** Extra trigger classes — the create form stretches it across its grid column. */
  className?: string;
};

type ScopeMarketSelectProps = SharedMarketSelectProps & {
  mode?: "scope";
  value: MarketScope;
  onChange: (scope: MarketScope) => void;
  /** Unique live reports — never the sum of per-market membership counts. */
  allMarketsReportCount: number;
};

/**
 * `scope` offers exclusive all-markets plus multi-select markets (hub default,
 * R7). `single` drops all-markets. `multi` is Score create and Change Market —
 * submit stays disabled until at least one id is selected.
 */
export type MarketSelectProps =
  | ScopeMarketSelectProps
  | (SharedMarketSelectProps & {
      mode: "single";
      value: string | null;
      onChange: (id: string | null) => void;
    })
  | (SharedMarketSelectProps & {
      mode: "multi";
      value: string[];
      onChange: (ids: string[]) => void;
    });

/**
 * Market selector for the Pre Flight hub and the create form: every market with
 * its report count, all-markets in scope mode, and a footer action onto the manage
 * panel in both modes (R7, R12, R14). Selection state stays with the caller so the
 * whole hub reads from one source (R8).
 */
export function MarketSelect(props: MarketSelectProps) {
  const { markets, disabled = false, size = "sm", className } = props;
  const { t } = useTranslation();
  const [isManageOpen, setIsManageOpen] = useState(false);

  const isMulti = props.mode === "multi";
  const isSingleSelect = props.mode === "single";
  const isScope = !isMulti && !isSingleSelect;
  const selectedIds = isMulti
    ? props.value
    : isScope
      ? (props.value ?? [])
      : props.value
        ? [props.value]
        : [];
  const selectedMarkets = selectedIds.flatMap((id) => {
    const market = markets.find((row) => row.id === id);
    return market ? [market] : [];
  });
  const firstSelected = selectedMarkets[0];

  const allMarketsLabel = t("preflight.markets.allMarkets", "All markets");
  const noneSelectedLabel = t("preflight.markets.noneSelected", "none selected");
  const multiPlaceholder = t(
    "preflight.markets.selectMarketsPlaceholder",
    "Select markets",
  );
  const singlePlaceholder = t("preflight.markets.selectPlaceholder", "Select a market");

  const selectedNames = selectedMarkets.map((market) => market.name).join(", ");
  const scopeLabel = isMulti
    ? selectedMarkets.length > 0
      ? selectedNames
      : multiPlaceholder
    : isScope && selectedMarkets.length > 0
      ? selectedNames
      : firstSelected?.name ??
        (isSingleSelect ? singlePlaceholder : allMarketsLabel);

  const triggerAria = isMulti
    ? t("preflight.markets.selectMarketsAria", "Markets: {{markets}}", {
        markets:
          selectedMarkets.length > 0
            ? selectedMarkets.map((market) => market.name).join(", ")
            : noneSelectedLabel,
      })
    : isSingleSelect
      ? t("preflight.markets.selectAria", "Market: {{market}}", {
          market: firstSelected?.name ?? noneSelectedLabel,
        })
      : t("preflight.markets.scopeAria", "Market scope: {{scope}}", {
          scope: scopeLabel,
        });

  const marketGroups = useMemo(() => groupMarketsByKind(markets), [markets]);

  function handleMarketRemoved(marketId: string) {
    if (isMulti) {
      props.onChange(props.value.filter((id) => id !== marketId));
      return;
    }
    if (isScope) {
      const current = props.value ?? [];
      if (!current.includes(marketId)) return;
      const next = current.filter((id) => id !== marketId);
      props.onChange(next.length === 0 ? null : next);
      return;
    }
    if (marketId === props.value) props.onChange(null);
  }

  function handleSingleSelect(marketId: string | null) {
    if (isMulti || isScope) return;
    props.onChange(marketId);
  }

  function handleAllMarkets() {
    if (!isScope) return;
    props.onChange(null);
  }

  function handleScopeToggle(marketId: string) {
    if (!isScope) return;
    const current = props.value ?? [];
    const next = current.includes(marketId)
      ? current.filter((id) => id !== marketId)
      : [...current, marketId];
    props.onChange(next.length === 0 ? null : next);
  }

  function handleMultiToggle(marketId: string) {
    if (!isMulti) return;
    props.onChange(
      props.value.includes(marketId)
        ? props.value.filter((id) => id !== marketId)
        : [...props.value, marketId],
    );
  }

  const isAllMarkets = isScope && (props.value == null || props.value.length === 0);
  const allMarketsReportCount = isScope ? props.allMarketsReportCount : 0;

  return (
    <>
      {/*
        modal={false}: when nested in a Radix Dialog, a modal menu sets
        body pointer-events:none via DismissableLayer. Closing that menu
        while the dialog also dismisses can leave the body unclickable
        (AD-2098). Non-modal menu avoids the second layer.
      */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={size}
            iconEnd="chevron-down"
            disabled={disabled}
            className={cn(
              "min-w-48 max-w-xs cursor-pointer justify-between overflow-hidden",
              className,
            )}
            aria-label={triggerAria}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
              {firstSelected ? (
                <MarketIcon
                  kind={firstSelected.kind}
                  code={firstSelected.code}
                  name={firstSelected.name}
                />
              ) : isScope ? (
                <ScopeDot selected square={isAllMarkets} />
              ) : null}
              <span
                className={cn(
                  "min-w-0 truncate",
                  (isSingleSelect || isMulti) &&
                    selectedMarkets.length === 0 &&
                    "text-muted-foreground",
                )}
              >
                {scopeLabel}
              </span>
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {isScope && (
            <>
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleAllMarkets();
                }}
                className="flex cursor-pointer items-center justify-between gap-2.5 font-medium"
              >
                <span className="inline-flex min-w-0 items-center gap-2.5">
                  <ScopeDot selected={isAllMarkets} square />
                  <span className="min-w-0 truncate">{allMarketsLabel}</span>
                </span>
                <ReportCount value={allMarketsReportCount} />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {marketGroups.length > 0 ? (
            marketGroups.map((group, groupIndex) => (
              <DropdownMenuGroup key={group.kind}>
                {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                <KindHeading kind={group.kind} />
                {group.markets.map((market) => {
                  const isSelected = selectedIds.includes(market.id);
                  const row = (
                    <span className="inline-flex min-w-0 items-center gap-2.5">
                      <ScopeDot selected={isSelected} />
                      <MarketIcon
                        kind={market.kind}
                        code={market.code}
                        name={market.name}
                      />
                      <span className="min-w-0 truncate">{market.name}</span>
                    </span>
                  );
                  if (isMulti || isScope) {
                    return (
                      <DropdownMenuCheckboxItem
                        key={market.id}
                        checked={isSelected}
                        onCheckedChange={() =>
                          isMulti
                            ? handleMultiToggle(market.id)
                            : handleScopeToggle(market.id)
                        }
                        onSelect={(event) => {
                          event.preventDefault();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        className="flex cursor-pointer items-center justify-between gap-2.5 pl-2 [&>span:first-child]:hidden"
                      >
                        {row}
                        <ReportCount value={market.reportCount} />
                      </DropdownMenuCheckboxItem>
                    );
                  }
                  return (
                    <DropdownMenuItem
                      key={market.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSingleSelect(market.id);
                      }}
                      className="flex cursor-pointer items-center justify-between gap-2.5"
                    >
                      {row}
                      <ReportCount value={market.reportCount} />
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            ))
          ) : (
            <div
              role="status"
              className="text-muted-foreground px-2 py-3 text-center text-xs"
            >
              {t(
                "preflight.markets.selectEmpty",
                "No markets yet — add one below.",
              )}
            </div>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              setIsManageOpen(true);
            }}
            className="text-primary cursor-pointer font-medium"
          >
            <Icon name="plus" size="sm" color="inherit" />
            <span>
              {t("preflight.markets.addOrManage", "Add or manage markets")}
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {isManageOpen ? (
        <ManageMarketsDialog
          open={isManageOpen}
          onOpenChange={setIsManageOpen}
          markets={markets}
          onMarketRemoved={handleMarketRemoved}
        />
      ) : null}
    </>
  );
}

/** Global is a single row — no section heading (same as the manage catalogue). */
function KindHeading({ kind }: { kind: PreflightMarketKind }) {
  const { t } = useTranslation();

  if (kind === "GLOBAL") return null;

  const label =
    kind === "CONTINENT"
      ? t("preflight.markets.groupContinents", "Continents")
      : t("preflight.markets.groupCountries", "Countries");

  return <DropdownMenuLabel>{label}</DropdownMenuLabel>;
}

/** Selection marker; the square shape distinguishes the all-markets roll-up from a market. */
function ScopeDot({
  selected,
  square = false,
}: {
  selected: boolean;
  square?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "size-2 shrink-0",
        square ? "rounded-xs" : "rounded-full",
        selected ? "bg-primary" : "bg-muted-foreground/35",
      )}
    />
  );
}

function ReportCount({ value }: { value: number }) {
  const { t } = useTranslation();
  return (
    <span
      className="text-muted-foreground shrink-0 text-xs tabular-nums"
      aria-label={t("preflight.markets.reportCount", {
        count: value,
        defaultValue_one: "{{count}} report",
        defaultValue_other: "{{count}} reports",
      })}
    >
      {value}
    </span>
  );
}
